import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, TrendingUp, BarChart3 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { historicoService } from '../../services/api';
import LoadingSpinner from './LoadingSpinner';

const FranquiaHistoricoModal = ({ isOpen, onClose, franquia, currentPeriod }) => {
  const [historicoData, setHistoricoData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Função para gerar os últimos 12 meses baseado no período atual
  const generateLast12Months = (currentPeriod) => {
    if (!currentPeriod) return [];
    
    const [mm, yyyy] = currentPeriod.split('-');
    const currentDate = new Date(parseInt(yyyy), parseInt(mm) - 1, 1);
    const months = [];
    
    // Gerar 6 meses antes e 5 meses depois do período atual
    for (let i = -6; i <= 5; i++) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() + i);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      months.push(`${month}-${year}`);
    }
    
    return months;
  };

  // Função para buscar dados históricos
  const fetchHistoricoData = async () => {
    if (!franquia || !currentPeriod) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Se for empresa, buscar histórico de empresa
      if (franquia.isEmpresa && franquia.ex_empresa) {
        const response = await historicoService.getHistoricoEmpresa(franquia.ex_empresa, franquia.id_franquia);
        const empresaData = response?.data || [];
        
        const formattedData = empresaData.map(item => ({
          periodo: item['mm-yyyy'],
          mes: item['mm-yyyy'].split('-')[0],
          ano: item['mm-yyyy'].split('-')[1],
          displayMonth: `${item['mm-yyyy'].split('-')[0]}/${item['mm-yyyy'].split('-')[1].slice(-2)}`,
          total_wil: item.total_locais || 0,
          total_pictor: item.total_cftv || 0,
          total: (item.total_locais || 0) + (item.total_cftv || 0),
          isCurrent: item['mm-yyyy'] === currentPeriod
        }));
        
        setHistoricoData(formattedData);
      } else {
        // Buscar histórico de franquia com dados por empresa
        const response = await historicoService.getHistoricoFranquiaComEmpresas(franquia.id_franquia);
        const franquiaData = response?.data || [];
        
        // Formatar dados para o gráfico
        const formattedData = franquiaData.map(item => {
          const empresasData = {};
          
          // Criar um objeto com dados de cada empresa
          item.empresas.forEach(emp => {
            empresasData[emp.nome_empresa] = (emp.total_locais || 0) + (emp.total_cftv || 0);
          });
          
          return {
            periodo: item.periodo,
            mes: item.periodo.split('-')[0],
            ano: item.periodo.split('-')[1],
            displayMonth: `${item.periodo.split('-')[0]}/${item.periodo.split('-')[1].slice(-2)}`,
            total_wil: item.total_wil || 0,
            total_pictor: item.total_pictor || 0,
            total: (item.total_wil || 0) + (item.total_pictor || 0),
            isCurrent: item.periodo === currentPeriod,
            ...empresasData // Adiciona dados de cada empresa como propriedades
          };
        });
        
        setHistoricoData(formattedData);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      setError('Erro ao carregar dados históricos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && franquia) {
      fetchHistoricoData();
    }
  }, [isOpen, franquia, currentPeriod]);

  // Calcular linha de tendência (regressão linear simples)
  const calculateTrendLine = (data) => {
    if (data.length < 2) return data;
    
    const n = data.length;
    const sumX = data.reduce((sum, _, index) => sum + index, 0);
    const sumY = data.reduce((sum, item) => sum + item.total, 0);
    const sumXY = data.reduce((sum, item, index) => sum + (index * item.total), 0);
    const sumXX = data.reduce((sum, _, index) => sum + (index * index), 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return data.map((item, index) => ({
      ...item,
      trend: Math.max(0, Math.round(slope * index + intercept))
    }));
  };

  const dataWithTrend = calculateTrendLine(historicoData);

  // Extrair nomes únicos de empresas para criar linhas dinâmicas
  const empresaNomes = React.useMemo(() => {
    if (!franquia?.isEmpresa && historicoData.length > 0) {
      const nomes = new Set();
      historicoData.forEach(item => {
        Object.keys(item).forEach(key => {
          if (!['periodo', 'mes', 'ano', 'displayMonth', 'total_wil', 'total_pictor', 'total', 'isCurrent', 'trend'].includes(key)) {
            nomes.add(key);
          }
        });
      });
      return Array.from(nomes);
    }
    return [];
  }, [historicoData, franquia]);

  // Cores para as linhas das empresas
  const cores = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ 
        position: 'fixed', 
        zIndex: 99999, 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-2 rounded-full bg-blue-100">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Histórico de 12 Meses
              </h3>
              <p className="text-sm text-gray-500">
                {franquia?.nome_franquia}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchHistoricoData}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Tentar Novamente
              </button>
            </div>
          ) : (
            <>
              {/* Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-600">Total WIL (12 meses)</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {historicoData.reduce((sum, item) => sum + item.total_wil, 0).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-orange-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-600">Total Pictor (12 meses)</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {historicoData.reduce((sum, item) => sum + item.total_pictor, 0).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-600">Média Mensal</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {Math.round(historicoData.reduce((sum, item) => sum + item.total, 0) / 12).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dataWithTrend} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="displayMonth" 
                      stroke="#666"
                      fontSize={12}
                      interval={0}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString('pt-BR')}
                    />
                    <Tooltip 
                      formatter={(value, name) => [
                        value.toLocaleString('pt-BR'), 
                        name === 'total_wil' ? 'WIL' : 
                        name === 'total_pictor' ? 'Pictor' : 
                        name === 'total' ? 'Total' : 'Tendência'
                      ]}
                      labelFormatter={(label) => `Período: ${label}`}
                    />
                    
                    {/* Linha de referência para o período atual */}
                    {dataWithTrend.find(item => item.isCurrent) && (
                      <ReferenceLine 
                        x={dataWithTrend.find(item => item.isCurrent).displayMonth}
                        stroke="#ef4444"
                        strokeDasharray="5 5"
                        label={{ value: "Atual", position: "top" }}
                      />
                    )}
                    
                    {/* Linhas do gráfico */}
                    {franquia?.isEmpresa ? (
                      // Gráfico de empresa individual
                      <>
                        <Line 
                          type="monotone" 
                          dataKey="total_wil" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="total_pictor" 
                          stroke="#f59e0b" 
                          strokeWidth={2}
                          dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </>
                    ) : (
                      // Gráfico de franquia com linhas por empresa
                      empresaNomes.map((nomeEmpresa, index) => (
                        <Line 
                          key={nomeEmpresa}
                          type="monotone" 
                          dataKey={nomeEmpresa}
                          stroke={cores[index % cores.length]}
                          strokeWidth={2}
                          dot={{ fill: cores[index % cores.length], strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      ))
                    )}
                    
                    {/* Linha de tendência */}
                    <Line 
                      type="monotone" 
                      dataKey="trend" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Legenda */}
              <div className="mt-4">
                {franquia?.isEmpresa ? (
                  // Legenda para empresa
                  <div className="flex justify-center space-x-6">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">WIL</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">Pictor</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-0.5 bg-indigo-500 mr-2" style={{ borderStyle: 'dashed' }}></div>
                      <span className="text-sm text-gray-600">Tendência</span>
                    </div>
                  </div>
                ) : (
                  // Legenda para franquia com empresas
                  <div>
                    <div className="flex flex-wrap justify-center gap-4">
                      {empresaNomes.map((nomeEmpresa, index) => (
                        <div key={nomeEmpresa} className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: cores[index % cores.length] }}
                          ></div>
                          <span className="text-xs text-gray-600">{nomeEmpresa}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-center mt-2">
                      <div className="flex items-center">
                        <div className="w-3 h-0.5 bg-indigo-500 mr-2" style={{ borderStyle: 'dashed' }}></div>
                        <span className="text-xs text-gray-600">Tendência</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FranquiaHistoricoModal;
